import { pathOr } from 'ramda';

import { setPage } from './setPage';
import { getTermValue, resetTerms, setTerm, TJsonQuery } from '../../../../packages/JsonQuery';
import { selectMapBounds } from '../../../Map/selectors';
import { IPublicWebV1SearchResponse } from '../../repositories/dailyrent/entities/response/PublicWebV1SearchResponse';
import { fetchListingOffers } from '../../services/listing/fetchListingOffers';
import { ITypedReduxAction, TThunkAction } from '../../types/redux';
import { actionGenerator } from '../../utils/redux/actionGenerator';
import { justChangeJsonQuery } from '../filters/justChangeJsonQuery';

export enum ELoadListingActionTypes {
  Request = 'loadListingOffers/request',
  Success = 'loadListingOffers/success',
  Failure = 'loadListingOffers/failure',
}

export type TLoadListingOffersMode = 'reset' | 'next' | 'prev';

export interface IRequestListingOffersPayload {
  reset: boolean;
}

export type TRequestListingOffers = ITypedReduxAction<ELoadListingActionTypes.Request, IRequestListingOffersPayload>;
export type TSuccessListingOffers = ITypedReduxAction<ELoadListingActionTypes.Success, IPublicWebV1SearchResponse>;
export type TFailureListingOffers = ITypedReduxAction<ELoadListingActionTypes.Failure>;

export const requestListingOffers: (payload: IRequestListingOffersPayload) => TRequestListingOffers = actionGenerator<
  ELoadListingActionTypes.Request,
  IRequestListingOffersPayload
>(ELoadListingActionTypes.Request);

export const successListingOffers: (data: IPublicWebV1SearchResponse) => TSuccessListingOffers = actionGenerator<
  ELoadListingActionTypes.Success,
  IPublicWebV1SearchResponse
>(ELoadListingActionTypes.Success);

export const failureListingOffers: () => TFailureListingOffers = actionGenerator<ELoadListingActionTypes.Failure>(
  ELoadListingActionTypes.Failure,
);

interface ILoadListingOffersArgs {
  mode?: TLoadListingOffersMode;
}

export const loadListingOffers =
  ({ mode = 'reset' }: ILoadListingOffersArgs = {}): TThunkAction<Promise<IPublicWebV1SearchResponse>> =>
  async (dispatch, getState, context) => {
    const { logger } = context;
    const state = getState();
    const { queryString } = state;
    let { jsonQuery } = state.filters;
    const { bounds } = selectMapBounds(state);

    const currentPage = pathOr(1, ['listingOffers', 'page'], state);
    const nextPageNumber = mode === 'next' ? currentPage + 1 : mode === 'prev' ? Math.max(1, currentPage - 1) : 1;

    jsonQuery = setTerm('page')(jsonQuery)(nextPageNumber);

    let jsonQueryToFetch = jsonQuery;

    if (bounds) {
      // нужно поменять местами широту и долготу, иначе search-engine выдает неправильный результат
      const reversedBounds = bounds.map(b => b.slice().reverse()) as [YMaps.TCoord, YMaps.TCoord];
      jsonQueryToFetch = setTerm('bbox')(jsonQuery)(reversedBounds);
    }

    try {
      dispatch(requestListingOffers({ reset: mode === 'reset' }));
      /*
       * отправляем queryString, чтобы после ответа бэка не терялись utm-метки из урлы
       * подробности в задаче https://jira.cian.tech/browse/CD-191441
       */
      const result = await fetchListingOffers(context, {
        jsonQuery: jsonQueryToFetch,
        queryString,
      });

      let nextJsonQuery = result.jsonQuery as TJsonQuery;
      nextJsonQuery = resetTerms(['bbox', 'id'])(nextJsonQuery);
      const responsePage = getTermValue('page')(nextJsonQuery) || 1;

      dispatch(successListingOffers(result));
      dispatch(justChangeJsonQuery({ nextJsonQuery }));
      dispatch(setPage(responsePage));

      return result;
    } catch (error) {
      dispatch(failureListingOffers());
      logger.error(error);

      throw error;
    }
  };
